/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CardTitle, CloseDialogBoxIcon, UpdateShiftDateWrapper, buttonStyles } from './UpdateShiftDate.Style';
import { Icons } from '../../../../Shared/Constants/Icons';
import { Controller, useForm } from 'react-hook-form';
import theme from '../../../../Shared/Themes/theme';
import DateSelection, { mandatoryField } from '../../../../Shared/Components/Common/DatePicker/DateSelection';
import { Box, Button, CircularProgress, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import { DatePickerContainer } from '../JobDetails/OneTimeJobDetails.Style';
import { FieldNames } from '../../Models/AddEditOneTimeJob.Model';
import {
  convertMinutesToHours,
  formatDate,
  getGraceMinutes,
  getTimeFormat,
  hours,
} from '../../../../Shared/Utilities/utils';
import { Options } from '../../../../Shared/Components/Common/Dropdown/Dropdown.styles';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { ErrorMsg } from '../AddEditOneTimeJob/AddEditOneTimeJob.Style';
import { IEmployees } from '../AssociateOnTheJob/AssociateOnTheJobAccordion';
import PunchInOutGracePeriod from '../../../Vendor/Components/AllJobs/CreateSchedule/PunchInOutGracePeriod';
import MultiSelectDropdown from './MultiSelectDropdown';

interface UpdateShiftDate {
  openUpdateShift: boolean;
  handleShiftDateClose: () => void;
  isDataLoading?: boolean;
  handleOnConfirm: (duration: any) => void;
  shiftData?: any;
  setStartTimeSelectOpen: (isOpen: boolean) => void;
  setEndTimeSelectOpen: (isOpen: boolean) => void;
  isStartTimeSelectOpen: boolean;
  isEndTimeSelectOpen: boolean;
  isMultiSelectedRows?: boolean;
  isMultipleRescheduleShift?: boolean;
  selectedRows?: any[];
  associates?: IEmployees[];
  selectedAssociates?: string[];
  setSelectedAssociates?: (selectedAssociates: string[]) => void;
}

const UpdateShiftDateCard = ({
  openUpdateShift,
  handleShiftDateClose,
  isDataLoading,
  handleOnConfirm,
  shiftData,
  setStartTimeSelectOpen,
  setEndTimeSelectOpen,
  isStartTimeSelectOpen,
  isEndTimeSelectOpen,
  isMultiSelectedRows,
  isMultipleRescheduleShift,
  selectedRows,
  associates,
  selectedAssociates,
  setSelectedAssociates,
}: UpdateShiftDate): JSX.Element => {
  const { t } = useTranslation(['altTexts', 'taskRejectionAndRework', 'rescheduleShiftDate, jobManagement']);
  const [hoursList, setHoursList] = useState<any[]>([]);
  const [isOffsetChecked, setIsOffsetChecked] = useState(false);
  const [offsetDays, setOffsetDays] = useState<number>();

  const [punchInHours, setPunchInHours] = useState<number>();
  const [punchInMinutes, setPunchInMinutes] = useState<number>();
  const [punchOutHours, setPunchOutHours] = useState<number>();
  const [punchOutMinutes, setPunchOutMinutes] = useState<number>();

  const { confirmButton, buttonText, textField } = buttonStyles;

  const { control, handleSubmit, getValues, watch, setValue, setError, clearErrors } = useForm({
    defaultValues: {
      shiftDate: new Date().toISOString(),
      startTime: shiftData?.startTime ?? shiftData?.plannedStartTime ?? '',
      endTime: shiftData?.endTime ?? shiftData?.plannedEndTime ?? '',
      offsetDays: 0,
      punchInGraceMinutes: undefined,
      punchOutGraceMinutes: undefined,
    },
  });

  const watchFields = watch();

  const punchInGraceMinutes = watch('punchInGraceMinutes');
  const punchOutGraceMinutes = watch('punchOutGraceMinutes');

  const handleDialogClose = (event: React.SyntheticEvent, reason?: string) => {
    if (reason && reason === 'backdropClick') return;
    handleShiftDateClose();
  };

  const handleOffsetCheckboxChange = () => {
    setIsOffsetChecked((prev) => !prev);
  };

  const handleOnPressConfirm = (duration: any) => {
    const timeDetails = {
      startTime: {
        hours: duration?.startTime?.split(':')[0] ?? '',
        minutes: duration?.startTime?.split(':')[1] ?? '',
      },
      endTime: {
        hours: duration?.endTime?.split(':')[0] ?? '',
        minutes: duration?.endTime?.split(':')[1] ?? '',
      },
      shiftDate: duration?.shiftDate,
      offsetDays: isOffsetChecked ? offsetDays : duration?.offsetDays,
      punchInGraceMinutes: getGraceMinutes(punchInHours, punchInMinutes) ?? undefined,
      punchOutGraceMinutes: getGraceMinutes(punchOutHours, punchOutMinutes) ?? undefined,
    };
    handleOnConfirm(timeDetails);
  };

  const handleOnPressRescheduleMultipleShifts = (duration: any) => {
    const timeDetails = {
      startTime: {
        hours: duration?.startTime?.split(':')[0] ?? '',
        minutes: duration?.startTime?.split(':')[1] ?? '',
      },
      endTime: {
        hours: duration?.endTime?.split(':')[0] ?? '',
        minutes: duration?.endTime?.split(':')[1] ?? '',
      },
      newDate: duration?.shiftDate,
      endDateOffset: isOffsetChecked ? offsetDays : duration?.offsetDays,
    };
    if (!isMultiSelectedRows) {
      const rescheduleShiftData = {
        workOrderId: shiftData?.workOrderId,
        workDayId: shiftData?.empShiftId,
        punchInGraceMinutes: getGraceMinutes(punchInHours, punchInMinutes) ?? undefined,
        punchOutGraceMinutes: getGraceMinutes(punchOutHours, punchOutMinutes) ?? undefined,
      };
      handleOnConfirm({ ...timeDetails, ...rescheduleShiftData });
    } else {
      const rescheduleWorkDayInput = selectedRows?.map((row) => ({
        workOrderId: row.workOrderId,
        workDayId: row.empShiftId,
        punchInGraceMinutes: getGraceMinutes(punchInHours, punchInMinutes) ?? undefined,
        punchOutGraceMinutes: getGraceMinutes(punchOutHours, punchOutMinutes) ?? undefined,
        ...timeDetails,
      }));
      handleOnConfirm({ rescheduleWorkDayInput });
    }
  };

  useEffect(() => {
    if (shiftData?.endDateOffset) {
      setOffsetDays(shiftData?.endDateOffset);
      setIsOffsetChecked(true);
    }
  }, [shiftData?.endDateOffset]);

  useEffect(() => {
    setHoursList(
      hours.map((hour) => {
        return {
          label: getTimeFormat(hour),
          value: hour,
        };
      })
    );
  }, []);

  const handleErrorForStartTime = (isError: boolean, message = '') => {
    if (isError) {
      setError(FieldNames.startTime, { type: 'validate', message });
    } else {
      clearErrors(FieldNames.startTime);
    }
  };

  const handleErrorForEndTime = (isError: boolean, message = '') => {
    if (isError) {
      setError(FieldNames.endTime, { type: 'validate', message });
    } else {
      clearErrors(FieldNames.endTime);
    }
  };

  const disableUpdateButton = () => {
    const { startTime, endTime, shiftDate } = getValues();
    if (!shiftDate || !startTime || !endTime) return true;

    const [year, month, day] = shiftDate.split('-').map(Number);

    const [startHours, startMinutes] = startTime.split(':').map(Number);
    const [endHours, endMinutes] = endTime.split(':').map(Number);

    const startDateTime = new Date(year, month - 1, day, startHours, startMinutes, 0);
    const endDateTime = new Date(year, month - 1, day, endHours, endMinutes, 0);

    const currentDateTime = new Date();

    if (endDateTime <= startDateTime) {
      handleErrorForEndTime(true, t('oneTimeJob:endTimeShouldBeGreaterThanStartTime'));
    } else {
      handleErrorForEndTime(false);
    }

    if (startDateTime < currentDateTime) {
      handleErrorForStartTime(true, t('oneTimeJob:startTimeShouldBeGreaterThanCurrentTime'));
    } else {
      handleErrorForStartTime(false);
    }

    return (
      !(startTime && endTime) ||
      endDateTime <= startDateTime ||
      startDateTime < currentDateTime ||
      (associates && isEmpty(selectedAssociates))
    );
  };

  useEffect(() => {
    disableUpdateButton();
  }, [watchFields]);

  useEffect(() => {
    if (isEmpty(shiftData)) return;

    const {
      scheduleStartTime,
      scheduleEndTime,
      scheduleDate,
      endDateOffset,
      workDate,
      shiftStartDateTime,
      shiftEndDateTime,
    } = shiftData;

    const shiftStartTime = scheduleStartTime ? scheduleStartTime : shiftStartDateTime;
    const shiftEndTime = scheduleEndTime ? scheduleEndTime : shiftEndDateTime;

    const startTime = shiftStartTime ? moment(shiftStartTime).utc().format('HH:mm') : '';
    const endTime = shiftEndTime ? moment(shiftEndTime).utc().format('HH:mm') : '';

    const shiftDate = scheduleDate
      ? formatDate(scheduleDate, 'YYYY-MM-DD')
      : workDate
        ? formatDate(workDate, 'YYYY-MM-DD')
        : '';

    setValue('shiftDate', shiftDate || '');
    setValue('startTime', startTime || '');
    setValue('endTime', endTime || '');
    setValue('offsetDays', endDateOffset || '');
    setValue('punchInGraceMinutes', shiftData?.punchInGraceMinutes);
    setValue('punchOutGraceMinutes', shiftData?.punchOutGraceMinutes);
  }, [shiftData, setValue]);

  useEffect(() => {
    if (punchInGraceMinutes || punchInGraceMinutes === 0) {
      const { hrs, mins } = convertMinutesToHours(punchInGraceMinutes);
      setPunchInHours(hrs);
      setPunchInMinutes(mins);
    }
    if (punchOutGraceMinutes || punchOutGraceMinutes === 0) {
      const { hrs, mins } = convertMinutesToHours(punchOutGraceMinutes);
      setPunchOutHours(hrs);
      setPunchOutMinutes(mins);
    }
  }, [punchInGraceMinutes, punchOutGraceMinutes]);

  return (
    <UpdateShiftDateWrapper open={openUpdateShift} onClose={handleDialogClose}>
      <CloseDialogBoxIcon onClick={handleShiftDateClose}>
        <img src={Icons.InfoWindowCloseIcon} alt={t('altTexts:closeIcon')} />
      </CloseDialogBoxIcon>
      <CardTitle>{t('rescheduleShiftDate:rescheduleShift')}</CardTitle>
      <DatePickerContainer>
        <DateSelection
          requiredText="Date is mandatory"
          control={control}
          fieldName={'shiftDate'}
          label={t('taskRejectionAndRework:selectDate')}
          isExcludePastDate={true}
          isExcludeFutureDate={false}
          dateWrapperstyles={{
            width: '22rem',
          }}
          textFieldStyles={{
            '& .MuiOutlinedInput-root': {
              '& fieldset,&:hover fieldset,&.Mui-focused fieldset': {
                borderWidth: 1,
                borderColor: theme.palette.primary.dark,
                borderRadius: '1rem',
              },
            },
            '& .MuiInputLabel-root': {
              color: theme.palette.primary.dark,
              '&.Mui-focused': {
                color: theme.palette.primary.dark,
              },
            },
          }}
        />
        {associates && associates?.length > 0 && (
          <MultiSelectDropdown
            label={t('jobManagement:associate')}
            isMandatory={true}
            dropdownValues={associates}
            onSelect={(selected) => setSelectedAssociates?.(selected)}
            showSelectAll={false}
          />
        )}
      </DatePickerContainer>
      <DatePickerContainer>
        <Controller
          control={control}
          name={FieldNames.startTime}
          defaultValue={shiftData?.startTime}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <TextField
                select
                fullWidth
                sx={{ ...textField, marginRight: '0' }}
                label={
                  <span>
                    {t('calendar:startTime')}
                    <span style={mandatoryField}> *</span>
                  </span>
                }
                value={value}
                onChange={(e) => onChange(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setStartTimeSelectOpen(!isStartTimeSelectOpen)} style={{ padding: 0 }}>
                        <img
                          src={isStartTimeSelectOpen ? Icons.DropdownDownIcon : Icons.DropdownUpIcon}
                          alt={isStartTimeSelectOpen ? 'down' : 'up'}
                        />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                SelectProps={{
                  open: isStartTimeSelectOpen,
                  IconComponent: () => null,
                  onOpen: () => setStartTimeSelectOpen(true),
                  onClose: () => setStartTimeSelectOpen(false),
                  MenuProps: {
                    PaperProps: {
                      style: {
                        boxShadow: '0px 16px 20px 0px rgba(58, 42, 0, 0.12)',
                      },
                    },
                  },
                }}>
                {hoursList.map((item) => (
                  <Options key={'startTime' + item.value} value={item.value}>
                    {item.label}
                  </Options>
                ))}
              </TextField>
              <ErrorMsg>{error && error?.message}</ErrorMsg>
            </Box>
          )}
        />
        <Controller
          control={control}
          name={FieldNames.endTime}
          defaultValue={shiftData?.endTime}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <TextField
                select
                sx={{ ...textField, marginRight: '0' }}
                label={
                  <span>
                    {t('calendar:endTime')}
                    <span style={mandatoryField}> *</span>
                  </span>
                }
                value={value}
                onChange={(e) => onChange(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setEndTimeSelectOpen(!isEndTimeSelectOpen)} style={{ padding: 0 }}>
                        <img
                          src={isEndTimeSelectOpen ? Icons.DropdownDownIcon : Icons.DropdownUpIcon}
                          alt={isEndTimeSelectOpen ? 'down' : 'up'}
                        />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                SelectProps={{
                  open: isEndTimeSelectOpen,
                  IconComponent: () => null,
                  onOpen: () => setEndTimeSelectOpen(true),
                  onClose: () => setEndTimeSelectOpen(false),
                  MenuProps: {
                    PaperProps: {
                      style: {
                        boxShadow: '0px 16px 20px 0px rgba(58, 42, 0, 0.12)',
                      },
                    },
                  },
                }}>
                {hoursList.map((item) => (
                  <Options key={'endTime' + item.value} value={item.value}>
                    {item.label}
                  </Options>
                ))}
              </TextField>
              <ErrorMsg>{error && error?.message}</ErrorMsg>
            </Box>
          )}
        />
      </DatePickerContainer>
      <PunchInOutGracePeriod
        punchInHours={punchInHours}
        setPunchInHours={setPunchInHours}
        punchInMinutes={punchInMinutes}
        setPunchInMinutes={setPunchInMinutes}
        punchOutHours={punchOutHours}
        setPunchOutHours={setPunchOutHours}
        punchOutMinutes={punchOutMinutes}
        setPunchOutMinutes={setPunchOutMinutes}
      />
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
          <IconButton onClick={handleOffsetCheckboxChange} sx={{ padding: 0 }}>
            <img
              src={isOffsetChecked ? Icons.CheckedIcon : Icons.Unchecked}
              alt={isOffsetChecked ? 'checked' : 'unchecked'}
            />
          </IconButton>
          <span style={{ marginLeft: '0.5rem' }}>{t('rescheduleShiftDate:addEndDateOffset')}</span>
        </Box>
        {isOffsetChecked && (
          <Controller
            control={control}
            name="offsetDays"
            render={({ field }) => (
              <TextField
                label={t('rescheduleShiftDate:shiftDaysMessage')}
                variant="outlined"
                fullWidth
                type="number"
                {...field}
                value={offsetDays}
                onChange={(e) => {
                  setOffsetDays(parseInt(e.target.value));
                }}
                sx={{ ...buttonStyles.textField, width: '100%', marginBottom: '1.5rem' }}
                InputProps={{
                  inputProps: { min: 0 },
                }}
              />
            )}
          />
        )}
      </Box>

      {isMultiSelectedRows && (
        <Box>
          <Typography sx={{ fontSize: theme.typography.h4?.fontSize, fontWeight: theme.typography.fontWeightBold }}>
            {t('taskRejectionAndRework:note')}
          </Typography>
          <Typography>
            {t('jobManagement:shiftsSelected')} <strong>{selectedRows?.length}</strong> {t('jobManagement:shifts')}
            {t('jobManagement:confirmText')}
          </Typography>
        </Box>
      )}
      <Button
        sx={{
          ...confirmButton,
          opacity: isDataLoading || disableUpdateButton() ? 0.5 : 1,
        }}
        disabled={isDataLoading || disableUpdateButton()}
        onClick={
          isMultipleRescheduleShift
            ? handleSubmit(handleOnPressRescheduleMultipleShifts)
            : handleSubmit(handleOnPressConfirm)
        }>
        {isDataLoading ? (
          <CircularProgress size={30} sx={{ color: theme.palette.common.white }} />
        ) : (
          <Typography sx={{ ...buttonText, color: theme.palette.common.white }}>
            {t('rescheduleShiftDate:confirm')}
          </Typography>
        )}
      </Button>
    </UpdateShiftDateWrapper>
  );
};

export default UpdateShiftDateCard;
